import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../db/shiksha-db';

const SUBJECTS = ['English', 'Hindi', 'Mathematics', 'Science', 'Social Science'];

const COMPONENTS_VI_VIII = [
  // Term 1 — CBSE standard: Periodic Test (10), Notebook (5), Subject Enrichment (5), Half Yearly (80)
  { id: 'pt1', label: 'Term 1: Periodic Test' },
  { id: 'notebook1', label: 'Term 1: Note Book' },
  { id: 'enrichment1', label: 'Term 1: Subject Enrichment' },
  { id: 'half_yearly', label: 'Term 1: Half Yearly Exam' },
  // Term 2 — CBSE standard: Periodic Test (10), Notebook (5), Subject Enrichment (5), Annual (80)
  { id: 'pt2', label: 'Term 2: Periodic Test' },
  { id: 'notebook2', label: 'Term 2: Note Book' },
  { id: 'enrichment2', label: 'Term 2: Subject Enrichment' },
  { id: 'annual', label: 'Term 2: Annual Exam' },
];

const COMPONENTS_IX = [
  { id: 'pre_mid', label: 'Pre-Mid Term' },
  { id: 'mid', label: 'Mid Term' },
  { id: 'post_mid', label: 'Post-Mid Term' },
  { id: 'multiple_assessment', label: 'Multiple Assessment' },
  { id: 'portfolio', label: 'Portfolio' },
  { id: 'enrichment', label: 'Subject Enrichment' },
  { id: 'annual', label: 'Annual Exam' },
];

export function useMarksCompleteness(classId, students = []) {
  // Determine cohort based on classId
  const isIXorX = classId === 'Class IX' || classId === 'Class X';
  const expectedComponents = isIXorX ? COMPONENTS_IX : COMPONENTS_VI_VIII;
  
  // We want to fetch all marks for these students
  const studentIds = students.map(s => s.id);
  
  const allMarks = useLiveQuery(
    () => {
      if (studentIds.length === 0) return [];
      return db.marks.where('studentId').anyOf(studentIds).toArray();
    },
    [studentIds.join(',')] // Re-run when student list changes
  ) || [];

  if (students.length === 0) {
    return {
      isComplete: false,
      completionPercent: 0,
      pendingSubjects: [],
      subjectStatus: {},
      totalSubjects: SUBJECTS.length
    };
  }

  const subjectStatus = {};
  const pendingSubjects = [];
  
  let totalRequiredEntries = SUBJECTS.length * expectedComponents.length * students.length;
  let filledEntries = 0;

  SUBJECTS.forEach(subject => {
    const missingComponents = [];
    let subjectFilledCount = 0;
    const subjectExpectedCount = expectedComponents.length * students.length;

    expectedComponents.forEach(comp => {
      const missingStudents = [];
      
      students.forEach(student => {
        // Find if this student has this mark for this subject
        // For cohort VI-VIII, marks page currently uses term state which corresponds to 'Term 1', 'Term 2', 'Pre-Board', 'Final'.
        // But the schema implies term is stored in mark.term and component in mark.componentId.
        // Actually looking at `ReportGenerator.jsx` evaluateAST, they fetch componentId exactly as 'pre_mid', 'pt1_a' etc.
        // The marks page sets componentId to whatever is configured in SUBJECT_COLUMNS.
        // Wait, MarksPage says: const columns = SUBJECT_COLUMNS[subjectId]
        // Currently it's mocked, but this hook expects exactly these components.
        // I will assume the component IDs match the ones used in ReportGenerator AST evaluation.
        
        const hasMark = allMarks.some(
          m => m.studentId === student.id && m.subject === subject && m.componentId === comp.id
        );
        
        if (hasMark) {
          filledEntries++;
          subjectFilledCount++;
        } else {
          missingStudents.push(student.name);
        }
      });
      
      if (missingStudents.length > 0) {
        missingComponents.push({
          componentId: comp.id,
          label: comp.label,
          missingCount: missingStudents.length,
          missingStudents: missingStudents
        });
      }
    });

    const isComplete = subjectFilledCount === subjectExpectedCount;
    
    subjectStatus[subject] = {
      isComplete,
      missingComponents,
      progress: Math.round((subjectFilledCount / subjectExpectedCount) * 100)
    };

    if (!isComplete) {
      pendingSubjects.push({
        subject,
        missingComponents,
        progress: subjectStatus[subject].progress
      });
    }
  });

  // Check academic status
  const studentsWithoutStatus = students.filter(s => !s.academicStatus);
  const isStatusComplete = studentsWithoutStatus.length === 0;

  if (!isStatusComplete) {
    pendingSubjects.push({
      subject: 'Admin Result Status',
      missingComponents: [{
        componentId: 'academicStatus',
        label: 'Result Status (Promoted/Failed)',
        missingCount: studentsWithoutStatus.length,
        missingStudents: studentsWithoutStatus.map(s => s.name)
      }],
      progress: Math.round(((students.length - studentsWithoutStatus.length) / students.length) * 100)
    });
  }

  const completionPercent = totalRequiredEntries === 0 
    ? 0 
    : Math.round((filledEntries / totalRequiredEntries) * 100);

  return {
    isComplete: isStatusComplete && completionPercent === 100 && totalRequiredEntries > 0,
    completionPercent: isStatusComplete ? completionPercent : Math.min(99, completionPercent),
    pendingSubjects,
    subjectStatus,
    totalSubjects: SUBJECTS.length
  };
}
